import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { CartErrorEvent } from '@theme/standard-events';
import { formatMoney, shopCurrencyAmountToPresentment } from '@scale/currency';
import { getCartSections } from '@scale/utilities';
import { buildBundleProperties, calculateDiscount, syncBundleRule } from '@scale/bundle-utils';

export function syncItemFromSelect(select, itemAttr, priceRef) {
  const item = select.closest(`[${itemAttr}]`);
  if (!item) return null;

  const option = select.options[select.selectedIndex];
  item.dataset.variantId = option.value;
  item.dataset.price = option.dataset.price;
  if (option.dataset.comparePrice) item.dataset.comparePrice = option.dataset.comparePrice;

  const priceEl = item.querySelector(`[data-ref="${priceRef}"]`);
  if (priceEl) priceEl.textContent = formatMoney(Number(option.dataset.price));

  return item;
}

export class BundleBase extends ScaleElement {
  #sectionId;
  #discountType;
  #discountValue;
  #offerId;
  #templateKey;
  #currentProductHandle;
  #bundleId;
  #bundleProof;
  #bundleProofDay;
  #authorizedGiftVariantId;
  #authorizedOfferId;

  get sectionId() { return this.#sectionId; }
  get discountType() { return this.#discountType; }
  get discountValue() {
    if (this.#discountType !== 'fixed_amount') return this.#discountValue;
    return shopCurrencyAmountToPresentment(this.#discountValue) ?? 0;
  }
  get offerId() { return this.#offerId; }
  get bundleId() { return this.#bundleId; }
  get bundleProof() { return this.#bundleProof; }
  get bundleProofDay() { return this.#bundleProofDay; }
  get authorizedGiftVariantId() { return this.#authorizedGiftVariantId; }
  get authorizedOfferId() { return this.#authorizedOfferId; }

  setup() {
    this.#sectionId = this.getAttribute('data-section-id');
    this.#discountType = this.getAttribute('data-discount-type') || 'none';
    this.#discountValue = Number(this.getAttribute('data-discount-value')) || 0;
    this.#offerId = this.getAttribute('data-offer-id');
    this.#templateKey = this.getAttribute('data-template-key');
    this.#currentProductHandle = this.getAttribute('data-current-product-handle') || undefined;
    this.setupBundle();
  }

  setupBundle() {}


  getCartContribution() {
    return { replace: false, items: this.#buildItems() };
  }

  prepareCartContribution() {
    return this.authorizeOffer(this.#offerId);
  }

  async authorizeOffer(offerId) {
    const bundleId = `${this.getBundleType()}-${this.#sectionId}-${crypto.randomUUID()}`;
    this.#bundleId = bundleId;
    this.#bundleProof = undefined;
    this.#bundleProofDay = undefined;
    this.#authorizedGiftVariantId = undefined;
    this.#authorizedOfferId = undefined;
    if (!offerId) return;
    const isPreview = this.hasAttribute('data-bundle-preview')
      || window.Shopify?.designMode
      || window.Shopify?.visualPreviewMode
      || document.documentElement.dataset.ovaPreview === 'true';
    if (isPreview) throw new Error(Scale.translations.bundlePreview);
    if (!this.#templateKey) throw new Error(Scale.translations.bundleUnverified, { cause: 'Bundle template context is missing.' });
    const authorization = await syncBundleRule({
      offerId,
      bundleId,
      templateKey: this.#templateKey,
      currentProductHandle: this.#currentProductHandle,
    });
    if (this.#bundleId !== bundleId) throw new Error(Scale.translations.bundleChanged);
    this.#bundleProof = authorization.proof;
    this.#bundleProofDay = authorization.proofDay;
    if (authorization.giftVariantId && !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(authorization.giftVariantId)) {
      throw new Error(Scale.translations.bundleUnverified, { cause: 'Bundle gift authorization is invalid.' });
    }
    this.#authorizedGiftVariantId = authorization.giftVariantId;
    this.#authorizedOfferId = offerId;
  }

  getContributionTotal() {
    const { replace, items } = this.getCartContribution();
    if (!items.length) return null;

    const subtotal = this.getActiveItems().reduce(
      (sum, item) => sum + Number(item.dataset.price) * (Number(item.dataset.quantity) || 1),
      0
    );

    return { replace, total: calculateDiscount(subtotal, this.#discountType, this.discountValue) };
  }


  async submitStandalone() {
    if (!this.isReadyToSubmit()) return;

    const atcBtn = this.getAtcButton();
    atcBtn?.setAttribute('aria-busy', 'true');

    let cartRequestStarted = false;
    try {
      await this.prepareCartContribution();
      const items = this.#buildItems();
      if (!items.length) return;
      cartRequestStarted = true;
      await CartAPI.add(items, {
        sections: getCartSections(),
        target: this,
        context: this.closest('dialog') ? 'dialog' : 'product',
      });
    } catch (error) {
      if (!cartRequestStarted) {
        this.dispatchEvent(new CartErrorEvent({
          error: error?.message || Scale.translations.cartError,
          code: 'INVALID',
        }));
      }
      this.announce(error?.message || Scale.translations.cartError);
    } finally {
      atcBtn?.removeAttribute('aria-busy');
    }
  }

  #buildItems() {
    const props = buildBundleProperties({
      type: this.getBundleType(),
      sectionId: this.#sectionId,
      name: this.getBundleName(),
      offerId: this.#offerId,
      bundleId: this.#bundleId,
      proof: this.#bundleProof,
      proofDay: this.#bundleProofDay,
    });

    return this.getActiveItems().map((item, i) => {
      const line = {
        id: Number(item.dataset.variantId),
        quantity: Number(item.dataset.quantity) || 1,
        properties: { ...props, _bundle_position: String(i + 1) },
      };
      if (item.dataset.sellingPlan) line.selling_plan = Number(item.dataset.sellingPlan);
      return line;
    });
  }


  getActiveItems() { return []; }
  getBundleType() { return ''; }
  getBundleName() { return ''; }
  getAtcButton() { return null; }
  isReadyToSubmit() { return this.getActiveItems().length > 0; }
}
